import { createApi } from '@reduxjs/toolkit/query/react'
import customFetchBase from '../services/CustomFetchBase'

/**
 * General API slice using RTK Query to handle common API requests.
 */
export const generalApi = createApi({
    reducerPath: 'generalApi',
    baseQuery: customFetchBase,
    endpoints: (builder) => ({
        /**
         * Mutation to fetch all countries.
         * @returns {Promise<any>} A list of all available countries.
         */
        getAllCountries: builder.mutation({
            query: () => ({
                url: `/country`,
                method: 'GET'
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to fetch states based on the selected country.
         * @param {string} country_id - The ID of the country.
         * @returns {Promise<any>} A list of states for the given country.
         */
        getStates: builder.mutation({
            query: (country_id) => ({
                url: `/state/?countryId=${country_id}`,
                method: 'GET'
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to initiate the forgot password process.
         * @param {Object} userData - The user data containing email or username.
         * @returns {Promise<any>} Response indicating if the reset link was sent.
         */
        forgetPassword: builder.mutation({
            query: (userData) => ({
                url: '/forgot-password',
                method: 'POST',
                body: userData
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to initiate the forgot username process.
         * @param {Object} userData - The user data containing email or phone number.
         * @returns {Promise<any>} Response indicating if the username was retrieved.
         */
        forgetUser: builder.mutation({
            query: (userData) => ({
                url: '/forget-user',
                method: 'POST',
                body: userData
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to create a new user.
         * @param {Object} userData - The user registration details.
         * @returns {Promise<any>} Response indicating if the user was created successfully.
         */
        createUser: builder.mutation({
            query: (userData) => ({
                url: '/create-user',
                method: 'POST',
                body: userData
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to check the status of a user.
         * @param {Object} userData - The user information.
         * @returns {Promise<any>} The user's status response.
         */
        userStatus: builder.mutation({
            query: (userData) => ({
                url: '/user-status',
                method: 'POST',
                body: userData
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to check if Salesforce account exists for a given email.
         * @param {string} email - The user's email address.
         * @returns {Promise<any>} Response indicating if the Salesforce account exists.
         */
        isSfAccountExisting: builder.mutation({
            query: (email) => ({
                url: `/isSfAccountExisting?email=${encodeURIComponent(email)}`,
                method: 'GET'
            }),
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        })
    })
})

/**
 * Auto-generated hooks for the defined mutations.
 */
export const {
    useGetAllCountriesMutation,
    useGetStatesMutation,
    useForgetUserMutation,
    useCreateUserMutation,
    useUserStatusMutation,
    useForgetPasswordMutation,
    useIsSfAccountExistingMutation
} = generalApi
